import {ApiBuilder} from "..";
import {Document, Response} from "@core/model";

const download = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url); // Libera memoria
};

const downloadDocument = (builder: ApiBuilder) =>
  builder.query<Blob, {id: string; name: string}>({
    query: request => {
      return {
        url: `/api/document/download/${request.id}`,
        responseHandler: async response => {
          const blob = await response.blob(); // Ottieni il Blob dal flusso
          download(blob, request.name);
        },
      };
    },
  });

const uploadDocuments = (builder: ApiBuilder) =>
  builder.mutation<
    Response<string>,
    {
      assetId?: string;
      taskId?: string;
      ticketId?: string;
      goodsId?: string;
      artifactId?: string;
      // sottotask dell'artifact: va inviato insieme ad artifactId
      artifactTaskId?: string;
      files: File[];
    }
  >({
    query: payload => {
      const formData = new FormData();
      payload.files.forEach(f => formData.append(`files`, f));
      if (payload.assetId) formData.append("assetId", payload.assetId);
      if (payload.taskId) formData.append("taskId", payload.taskId);
      if (payload.ticketId) formData.append("ticketId", payload.ticketId);
      if (payload.goodsId) formData.append("goodsId", payload.goodsId);
      if (payload.artifactId) formData.append("artifactId", payload.artifactId);
      if (payload.artifactTaskId)
        formData.append("artifactTaskId", payload.artifactTaskId);

      return {
        url: "/api/document/upload",
        method: "POST",
        body: formData,
      };
    },
    invalidatesTags: [{type: "document"}],
  });

const deleteDocuments = (builder: ApiBuilder) =>
  builder.mutation<void, string[]>({
    query: body => ({
      url: "/api/document",
      method: "DELETE",
      body,
    }),
    invalidatesTags: [{type: "document"}],
  });

const tagDocument = (builder: ApiBuilder) =>
  builder.mutation<void, {attachmentId: string; tag: string}>({
    query: ({attachmentId, tag}) => ({
      url: `/api/document/tag/${attachmentId}?value=${tag}`,
      method: "PATCH",
    }),
  });

/**
 * Criteri di ricerca dei documenti: artifactId comprende i documenti dei sottotask,
 * unassigned restituisce solo quelli senza asset né artifact.
 */
type DocumentSearch = {
  name?: string;
  assetId?: string;
  artifactId?: string;
  artifactTaskId?: string;
  unassigned?: boolean;
  tags?: string[];
};

// tutti i documenti, dal più recente: la ricerca non è paginata
const findDocuments = (builder: ApiBuilder) =>
  builder.query<Document[], DocumentSearch>({
    query: request => {
      const {name, assetId, artifactId, artifactTaskId, unassigned, tags} =
        request;

      const params = new URLSearchParams();
      if (name) params.append("name", name);
      if (assetId) params.append("assetId", assetId);
      if (artifactId) params.append("artifactId", artifactId);
      if (artifactTaskId) params.append("artifactTaskId", artifactTaskId);
      if (unassigned) params.append("unassigned", "true");
      tags?.forEach(t => params.append("tags", t));

      return {
        url: `/api/document/search?${params.toString()}`,
        method: "GET",
      };
    },
    transformResponse: (r: Response<Document[]>) => r.data ?? [],
    providesTags: result => (result ? [{type: "document"}] : []),
  });

export type {DocumentSearch};
export {
  downloadDocument,
  uploadDocuments,
  deleteDocuments,
  tagDocument,
  findDocuments,
};
